import { useEffect } from 'react';
import { useTrialStore } from '@/stores/trial-store';
import { useLicenseStore } from '@/stores/license-store';
import { useAppStore } from '@/stores/app-store';
import { useAuthStore } from '@/stores/auth-store';
import { useStoreStore } from '@/stores/store-store';
import { LICENSING_EXCLUDED_APPS, ADMIN_UIDS } from '@/config/constants';

export type TrialBadgeStatus = 'paid' | 'trial' | 'expired' | 'excluded';

function isInsideOwnAppFrame() {
  try {
    return window.parent !== window && window.parent.location.origin === window.location.origin;
  } catch {
    return false;
  }
}

/**
 * Convenience hook that composes trial state, subscription state,
 * and app context into a single API for trial/subscription gating.
 *
 * App-scoping: subs are matched to the current app by `extensionName`
 * (e.g. "whatsappwebforsalesforce"), which is the format used in both
 * the license API's `appCode` field and Stripe's `stripe_metadata_appcode`.
 */
export function useTrial() {
  const trialStartTime = useTrialStore((s) => s.trialStartTime);
  const trialLoaded = useTrialStore((s) => s.trialLoaded);
  const trialDaysRemaining = useTrialStore((s) => s.trialDaysRemaining);
  const isTrialExpired = useTrialStore((s) => s.isTrialExpired);
  const isTrialActive = useTrialStore((s) => s.isTrialActive);

  const licenseSubscriptions = useLicenseStore((s) => s.licenseSubscriptions);
  const adminSubscriptions = useStoreStore((s) => s.subscriptions);
  const licenseFreshFetched = useLicenseStore((s) => s.licenseFreshFetched);
  const subscriptionsLoaded = useStoreStore((s) => s.subscriptionsLoaded);
  const productsLoadedForApps = useStoreStore((s) => s.productsLoadedForApps);
  const loadSubscriptions = useStoreStore((s) => s.loadSubscriptions);
  const loadProducts = useStoreStore((s) => s.loadProducts);
  const appCodeTPA = useAppStore((s) => s.appCodeTPA);
  const extensionName = useAppStore((s) => s.extensionName);
  const user = useAuthStore((s) => s.user);
  const selectedAdminUserId = useAuthStore((s) => s.selectedAdminUserId);

  const isSharedAccess = !!selectedAdminUserId;
  const currentAppCode = (extensionName || '').toLowerCase();
  const productsLoadedForCurrentApp = !!extensionName && !!productsLoadedForApps[extensionName];

  // Sub-users don't own subs, so skip loading their `customers/{uid}/subscriptions`.
  // For admins/owners, we need owned subs to detect activation-pending state.
  const needsOwnedSubs = !isSharedAccess && !!user?.uid;

  // Auto-load owned subs and products. Both stores dedupe internally via
  // `subscriptionsLoaded` and `productsLoadedForApps`, so calling these from
  // multiple components is safe (no duplicate fetches, no retry loops).
  useEffect(() => {
    if (needsOwnedSubs && !subscriptionsLoaded) {
      loadSubscriptions(user!.uid);
    }
    if (extensionName && !productsLoadedForCurrentApp) {
      loadProducts(extensionName);
    }
  }, [
    needsOwnedSubs,
    subscriptionsLoaded,
    loadSubscriptions,
    user,
    extensionName,
    productsLoadedForCurrentApp,
    loadProducts,
  ]);

  // Stripe (owned subs from `customers/{uid}/subscriptions`) is the source of
  // truth for "did the admin pay?" Licensing API is the source of truth for
  // "is a license seat allocated to this user?" An admin who paid but hasn't
  // assigned themselves a seat → activation pending.
  const hasActiveStripeSub = adminSubscriptions.some((s) => {
    const isActive = s.status === 'active' || s.status === 'trialing';
    const subAppCode = (s.productData?.stripe_metadata_appcode || '').toLowerCase();
    return isActive && subAppCode === currentAppCode;
  });

  const hasActiveLicenseSub = licenseSubscriptions.some((s) => {
    const isActive = s.subscriptionStatus === 'active' || s.subscriptionStatus === 'trialing';
    const subAppCode = (s.appCode || '').toLowerCase();
    return isActive && subAppCode === currentAppCode;
  });

  const hasActiveSubscription = hasActiveStripeSub || hasActiveLicenseSub;

  // Activation needed: admin paid for this app but no license seat is active
  // for the current user. Only meaningful for non-shared-access (the owner);
  // for sub-users, hasActiveStripeSub is always false (they don't own subs).
  const isActivationRequired = hasActiveStripeSub && !hasActiveLicenseSub;

  // Combine sources for cancellation detection: a cancelled sub may show up
  // in either licensing API (as inactive sub allocated to user) or Stripe
  // (as the admin's cancelled owned sub). Both sources are matched strictly
  // by appCode === current app — a cancelled sub for another app must NOT
  // surface here even if its product metadata is briefly missing during the
  // window between loadSubscriptions and loadProducts/enrichSubscriptions.
  const cancelledInLicenseApi = licenseSubscriptions.some((s) => {
    const isCancelled = (s.subscriptionStatus || '').toLowerCase().includes('cancel');
    const subAppCode = (s.appCode || '').toLowerCase();
    return isCancelled && subAppCode === currentAppCode;
  });

  const cancelledInOwnedSubs = adminSubscriptions.some((s) => {
    const isCancelled = (s.status || '').toLowerCase().includes('cancel');
    const subAppCode = (s.productData?.stripe_metadata_appcode || '').toLowerCase();
    return isCancelled && subAppCode === currentAppCode;
  });

  const hasCancelledSubscription = cancelledInLicenseApi || cancelledInOwnedSubs;

  const isExcludedApp = LICENSING_EXCLUDED_APPS.has(appCodeTPA || '');
  const isAdmin = user ? ADMIN_UIDS.has(user.uid) : false;
  const isLookupInOwnInbox = appCodeTPA === 'lookup' && isInsideOwnAppFrame();

  // Bypass all trial/licensing for excluded apps and Ulgebra system admins
  const licensingApplies = !isExcludedApp && !isAdmin && !isLookupInOwnInbox;

  // Gate UI on having all data we need:
  // - trialLoaded: trial timestamp resolved
  // - licenseFreshFetched: license API has returned (not just stale cache)
  // - subscriptionsLoaded + products loaded: only required for admins/owners
  //   (sub-users don't load owned subs). Without products, every owned sub
  //   has `productData=undefined` → empty stripe_metadata_appcode → looks
  //   like "no active sub for this app" → trial-expired banner flickers
  //   for ~500ms before products arrive and flip it to activation banner.
  const ownerDataReady =
    !needsOwnedSubs || (subscriptionsLoaded && productsLoadedForCurrentApp);
  const allDataReady = trialLoaded && licenseFreshFetched && ownerDataReady;

  // Show warning banner when trial is active with 3 or fewer days left
  const shouldShowTrialBanner =
    licensingApplies &&
    allDataReady &&
    isTrialActive &&
    trialDaysRemaining <= 3 &&
    !hasActiveSubscription;

  // Trial-expired banner — trial expired AND no sub history at all for this app
  const shouldShowExpiredBanner =
    licensingApplies &&
    allDataReady &&
    isTrialExpired &&
    !hasActiveSubscription &&
    !hasCancelledSubscription;

  // Activation banner — paid but no active license seat. Only meaningful for
  // the license owner; sub-users see the contact-admin paywall instead.
  const shouldShowActivationBanner =
    licensingApplies &&
    allDataReady &&
    isActivationRequired;

  // Cancelled banner — trial expired AND a cancelled sub for this app exists
  const shouldShowCancelledBanner =
    licensingApplies &&
    allDataReady &&
    isTrialExpired &&
    !hasActiveSubscription &&
    hasCancelledSubscription;

  // Paywall (modal) — only when trial is actually expired AND no active sub.
  // Active-trial users with a cancelled prior sub still have trial access,
  // so we don't block them.
  const shouldShowPaywall =
    licensingApplies &&
    allDataReady &&
    isTrialExpired &&
    !hasActiveSubscription;

  const isPaid = hasActiveSubscription;

  const badgeStatus: TrialBadgeStatus = isExcludedApp
    ? 'excluded'
    : isPaid
      ? 'paid'
      : isTrialActive
        ? 'trial'
        : 'expired';

  return {
    // Trial state
    trialStartTime,
    trialLoaded,
    trialDaysRemaining,
    isTrialExpired,
    isTrialActive,

    // Subscription state (filtered to current app)
    hasActiveSubscription,
    hasCancelledSubscription,
    isActivationRequired,
    isPaid,

    // Context
    isExcludedApp,
    isAdmin,
    licensingApplies,
    isSharedAccess,

    // UI triggers
    shouldShowPaywall,
    shouldShowTrialBanner,
    shouldShowExpiredBanner,
    shouldShowCancelledBanner,
    shouldShowActivationBanner,
    badgeStatus,
  };
}
